import React, { memo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';

import { Fab } from '../material-ui/Fab';
import { IconButton } from '../material-ui/IconButton';
import { ThreadList } from './ThreadList/ThreadList';
import { ThreadView } from './ThreadView/ThreadView';
import { ComposeDialog } from './ComposeDialog/ComposeDialog';
import { GroupMembersDialog } from './GroupMembersDialog/GroupMembersDialog';
import { triggerAttachmentDownload } from './attachments';
import { useMessengerPage } from './useMessengerPage';
import { useStyles } from './useStyles';

export const MessengerPage = memo(() => {
    const classes = useStyles();
    const { t: translate } = useTranslation();
    const {
        isNarrow,
        bottomInset,
        isFlightScoped,
        chats,
        search,
        activeChat,
        messages,
        recipients,
        composeOpen,
        imagePreview,
        membersChat,
        groupMembers,
        handleSearch,
        handleSelect,
        handleBack,
        handleSend,
        handleOpenAttachment,
        getAttachmentData,
        handleOpenImagePreview,
        handleCloseImagePreview,
        handleOpenCompose,
        handleCloseCompose,
        handleSelectRecipient,
        handleCreateGroup,
        handleOpenMembers,
        handleCloseMembers,
        handleAddMembers,
    } = useMessengerPage();

    const showList = !isFlightScoped && (!isNarrow || !activeChat);
    const showThread = isFlightScoped || !isNarrow || !!activeChat;

    return (
        <div className={classes.page} style={{ '--messenger-bottom-inset': `${bottomInset}px` }}>
            {showList && (
                <ThreadList
                    className={classnames(classes.list, isNarrow && classes.threadNarrowFull, isNarrow && classes.listWithFab)}
                    chats={chats}
                    activeChat={activeChat}
                    search={search}
                    onSearch={handleSearch}
                    onSelect={handleSelect}
                />
            )}
            {showThread && (
                <ThreadView
                    className={classnames(classes.thread, isNarrow && classes.threadNarrowFull)}
                    chat={activeChat}
                    messages={messages}
                    isNarrow={isNarrow}
                    bottomInset={bottomInset}
                    onBack={isFlightScoped ? undefined : handleBack}
                    onSend={handleSend}
                    onOpenAttachment={handleOpenAttachment}
                    getAttachmentData={getAttachmentData}
                    onPreviewImage={handleOpenImagePreview}
                    onOpenMembers={handleOpenMembers}
                />
            )}
            {showList && (
                <Fab
                    color="primary"
                    className={classnames(classes.composeFab, isNarrow && classes.composeFabNarrow)}
                    onClick={handleOpenCompose}
                >
                    <AddIcon />
                </Fab>
            )}
            <ComposeDialog
                open={composeOpen}
                recipients={recipients}
                onSelect={handleSelectRecipient}
                onCreateGroup={handleCreateGroup}
                onClose={handleCloseCompose}
            />
            <GroupMembersDialog
                open={!!membersChat}
                chat={membersChat}
                members={groupMembers}
                recipients={recipients}
                onAdd={handleAddMembers}
                onClose={handleCloseMembers}
            />
            {imagePreview && (
                <div className={classes.lightbox} onClick={handleCloseImagePreview} role="presentation">
                    <div className={classes.lightboxActions} onClick={(event) => event.stopPropagation()} role="presentation">
                        <IconButton
                            className={classes.lightboxButton}
                            onClick={() => triggerAttachmentDownload({ preview: imagePreview.src, name: imagePreview.name })}
                            title={translate('messenger.download')}
                        >
                            <GetAppIcon />
                        </IconButton>
                        <IconButton
                            className={classes.lightboxButton}
                            onClick={handleCloseImagePreview}
                            title={translate('messenger.close')}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <img
                        className={classes.lightboxImage}
                        src={imagePreview.src}
                        alt={imagePreview.name}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
});

MessengerPage.displayName = 'MessengerPage';
